import { gsap } from 'gsap';
import AbstractUnit from './AbstractUnit';
import { GeometriesFactory } from '../../factories/GeometriesFactory';
import { MaterialsFactory } from '../../factories/MaterialsFactory';
import { TexturesStorage } from '../../factories/TexturesStorage';
import { COLOR_SCHEMES } from '../../../../constants/colors';
import {
    REF_SIZE, REF_SIZE_PT, SIZE_OPTIONS, UNIT
} from '../../../../constants/size';

const THREE = require('three');

class PreviewUnit extends AbstractUnit {
    constructor(data, webgl, markers) {
        super(data, webgl, null, null, markers);
    }

    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        this.hasInvalidate = true;
        this.hasResize = true;
        this.canFocusAt = true;
        this._moveHandler = null;
        this._orientation = '';
        this._ready = false;
        this._overStateScale = data?.overStateScale || 1.1;

        this._dioganalRatio = 1;
        if (data) {
            const { width, height, scale } = data.geometry;

            this._dioganal = scale * Math.sqrt(width * width + height * height);
        }

        this._webgl = webgl;
    }

    async _constructView() {
        const {
            geometry, type, scheme, textures
        } = this.data;

        const schemeConfig = COLOR_SCHEMES[scheme];
        const { color0, color1 } = schemeConfig.previewGradientMap;

        const texture = await TexturesStorage.getTexture({ options: textures }, this.name);
        const geom = await GeometriesFactory.create(geometry.type, geometry);

        const material = MaterialsFactory.create({
            color0,
            color1,
            tintInfluence: 1,
            animInfluence: 1,
            map: texture
        }, type);

        const root = new THREE.Group();
        const mesh = new THREE.Mesh(geom, material);

        root.position.set(
            0,
            0,
            -this.mainViewZ
        );
        root.add(mesh);
        this.add(root);

        this.root = root;
        this.mainView = mesh;
        this.mainClickTarget = [mesh];
        this.clickActions = [this.data.action];
        this.actionKey = this.data.action?.key;
        this.mainMaterial = material;
        this.videoTexture = texture instanceof THREE.VideoTexture ? texture : null;

        this.mainView.layers.set(this._layerChannel);

        this._ready = true;

        this._updateVideoTexture();
        this.resize(undefined, undefined, this._orientation, true);
    }

    start() {
        super.start();

        if (this.clickable) {
            const target = this.mainView || this;

            target.cursor = 'pointer';
            target.on('mouseover', () => {
                this.onOver(target);
            });
            target.on('mouseout', () => {
                this.onOut(target);
            });
        }
    }

    resize(winWidth, winHeight, orientation, force) {
        if (!this._ready) {
            this._orientation = orientation;

            return;
        }

        if (this._orientation === orientation && !force) return;

        let { position } = this.data;
        const sizeRef = orientation === SIZE_OPTIONS.LANDSCAPE ? REF_SIZE : REF_SIZE_PT;

        position = position ?? { x: 0, y: 0 };

        let x = position.x * sizeRef.w * sizeRef.ratio.x;
        let y = position.y * sizeRef.h * sizeRef.ratio.y;

        if (orientation === SIZE_OPTIONS.PORTRAIT) {
            const l = Math.sqrt(x * x + y * y);
            const minL = sizeRef.w * UNIT.sizesScaleRatio * 0.88;

            if (l < minL) {
                x += (x * Math.abs(minL - l)) / Math.abs(x);
                y += (y * Math.abs(minL - l)) / Math.abs(y);
            }
        }

        this.root.position.set(
            x,
            y,
            -this.mainViewZ
        );
        this.root.scale.set(
            sizeRef.previewScale,
            sizeRef.previewScale,
            1
        );

        const refD = UNIT.sizesScaleRatio * Math.sqrt(sizeRef.w * sizeRef.w + sizeRef.h * sizeRef.h);

        this._dioganalRatio = this._dioganal / refD;

        this._orientation = orientation;
    }

    focusAt(actionElemIdOrName) {
        if (!this.clickable) return;
        if (actionElemIdOrName !== this.actionKey) return;

        const target = this.mainView || this;

        this.onOver(target);
    }

    blur(actionElemIdOrName) {
        if (!this.clickable) return;
        if (actionElemIdOrName !== this.actionKey) return;

        const target = this.mainView || this;

        this.onOut(target);
    }

    onOver(target) {
        const scale = this._overStateScale;
        const duration = 0.75;

        gsap.killTweensOf(this.mainMaterial);
        gsap.killTweensOf(this.mainView.scale);

        gsap.to(this.mainMaterial, {
            duration,
            tintInfluence: 0.55,
            animInfluence: 0
        });
        gsap.to(this.mainView.scale, {
            duration,
            x: scale,
            y: scale
        });

        this._moveHandler = (evt) => {
            this.onMove(evt);
        };

        if (target) target.on('mousemove', this._moveHandler);
    }

    onOut(target) {
        const duration = 0.45;

        gsap.killTweensOf(this.mainMaterial);
        gsap.killTweensOf(this.mainView.scale);

        gsap.to(this.mainMaterial, {
            duration,
            tintInfluence: 1,
            animInfluence: 1
        });
        gsap.to(this.mainView.scale, {
            x: 1,
            y: 1
        });

        this._moveTo();

        if (this._moveHandler !== null) target.off('mousemove', this._moveHandler);
        this._moveHandler = null;
    }

    onMove(evt) {
        this.updateMatrixWorld();

        const { camera } = this._webgl;
        const vectorMouse = evt.data.global;
        let vector = new THREE.Vector3();
        const mtx = new THREE.Matrix4();

        vector.setFromMatrixPosition(this.root.matrixWorld);

        vector = vector.applyMatrix4(camera.matrixWorldInverse).applyMatrix4(mtx.copy(camera.projectionMatrix));
        vector.set(
            (vectorMouse.x - vector.x) / this._dioganalRatio,
            (vectorMouse.y - vector.y) / this._dioganalRatio,
            0
        );

        this._moveTo(vector);
    }

    _moveTo(vector = null) {
        vector = vector || new THREE.Vector3(0, 0, 0);

        gsap.killTweensOf(this.mainView.position);
        gsap.killTweensOf(this.mainView.rotation);

        gsap.to(this.mainView.position, {
            duration: 0.94,
            ease: 'sine.out',
            x: vector.x * 0.03 * UNIT.zSize,
            y: vector.y * 0.03 * UNIT.zSize
        });
        gsap.to(this.mainView.rotation, {
            duration: 0.94,
            ease: 'sine.out',
            x: (vector.x * 10 * Math.PI) / 180,
            y: (vector.y * 10 * Math.PI) / 180
        });
    }

    async _updateVideoTexture() {
        if (!this.videoTexture || !this.videoTexture.image) return;

        const videoEl = this.videoTexture.image;

        if (!(videoEl instanceof HTMLVideoElement)) return;

        if (this.enabled) {
            // console.log('PLAY VIDEO', this.videoTexture);

            try {
                await videoEl.play();
            } catch (e) {
                console.warn(e, this.videoTexture);
            }

            const hasVideoFrameCallback = 'requestVideoFrameCallback' in videoEl;

            if (hasVideoFrameCallback) {
                const checkUpdate = () => {
                    videoEl.setAttribute('data-has-frame-callback', 'true');
                };

                videoEl.requestVideoFrameCallback(checkUpdate);
            }

            return;
        }

        videoEl.pause();

        // console.log('PAUSE VIDEO', this.videoTexture);
    }

    invalidate(t, dt) {
        super.invalidate(t, dt);
        if (this.mainMaterial) this.mainMaterial.uniforms.time.value = t;
    }

    set enabled(value) {
        const changed = super.enabled !== value;

        if (!changed) return;

        super.enabled = value;
        this._updateVideoTexture();
    }

    get enabled() {
        return super.enabled;
    }
}

export default PreviewUnit;
